import { getHealthScoreLabel, getHealthScoreColor } from '@nirvik/shared';
import { useEffect, useState } from 'react';
import { useCssVars } from '@/hooks/use-css-vars';

interface HealthScoreRingProps {
  score: number | null;
  size?: number;
  strokeWidth?: number;
}

export function HealthScoreRing({ score, size = 120, strokeWidth = 8 }: HealthScoreRingProps) {
  const [animatedScore, setAnimatedScore] = useState(0);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (animatedScore / 100) * circumference;
  const color = score !== null ? getHealthScoreColor(score) : '#e5e7eb';

  // Compact mode for small rings (portfolio cards, etc.) \u2014 hide the "/ 100"
  // sub-label and the descriptive label below since neither fits without
  // visual clutter, and scale the score number to the ring size.
  const compact = size < 90;
  const scoreFontPx = Math.round(size * 0.36); // ring 56 \u2192 20px, ring 140 \u2192 50px
  const unitFontPx = Math.round(size * 0.10);

  // Dynamic ring size + score text color flow through CSS custom properties
  // so we don't need a `style="..."` attribute (CSP style-src-attr is strict).
  const ringRef = useCssVars<HTMLDivElement>({
    '--ring-size': `${size}px`,
    '--score-color': color,
    '--score-font': `${scoreFontPx}px`,
    '--unit-font': `${unitFontPx}px`,
  });

  useEffect(() => {
    if (score === null) return;
    const timer = setTimeout(() => setAnimatedScore(score), 100);
    return () => clearTimeout(timer);
  }, [score]);

  return (
    <div className="flex flex-col items-center gap-3">
      <div ref={ringRef} className="relative w-[var(--ring-size)] h-[var(--ring-size)]">
        <svg width={size} height={size} className="-rotate-90">
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            className="stroke-gray-200 dark:stroke-gray-700"
            stroke="currentColor"
            strokeWidth={strokeWidth}
          />
          {/* Score arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
          <span className="font-black tracking-tight text-[color:var(--score-color)] text-[length:var(--score-font)]">
            {score !== null ? score : '\u2014'}
          </span>
          {!compact && (
            <span className="text-muted-foreground dark:text-muted-foreground font-semibold uppercase tracking-wide mt-1 text-[length:var(--unit-font)]">
              / 100
            </span>
          )}
        </div>
      </div>
      {!compact && (
        <p className="text-sm font-semibold text-muted-foreground dark:text-foreground text-center max-w-48">
          {score !== null ? getHealthScoreLabel(score) : 'Not scanned yet'}
        </p>
      )}
    </div>
  );
}
